/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react-hooks/rules-of-hooks */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { feedbackClass, hasFeedback } from 'utils/formValidations';
import 'styles/components/formRenderers';
import { InputRedux, SelectRedux, FilterableSelect, InputCardRedux } from '@reservamos/elements';
import LabeledToggle from '../ui/atoms/LabeledToggle';

/**
 * fieldRenderer component.
 * Renders a form input field with validation and error handling.
 *
 * @param {object} props - input, meta, type, placeholder, readonly, onClick, cls, maxLength, isDisabled, pattern, inputMode, placeholderOnly, label, id, onClickClear, autoComplete, useCardInput
 * @param {object} props.input - Redux Form input object
 * @param {object} props.meta - Redux Form meta object containing validation state
 * @param {string} props.type - Input type
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} [props.readonly=false] - Whether the input is read-only
 * @param {Function} [props.onClick] - Click handler function
 * @param {string} [props.cls=''] - Additional CSS class names
 * @param {string} [props.maxLength] - Maximum input length
 * @param {boolean} [props.isDisabled=false] - Whether the input is disabled
 * @param {boolean} [props.isLoading=false] - Shows a spinner inside the input, for background lookups
 * @param {string} [props.pattern] - Input pattern for validation
 * @param {string} [props.inputMode] - Input mode
 * @param {boolean} [props.placeholderOnly] - Show only placeholder
 * @param {string} [props.label] - Input label
 * @param {string} [props.id] - Input ID
 * @param {Function} [props.onClickClear] - Clear input handler
 * @param {string} [props.autoComplete] - Value for the input's autocomplete attribute
 * @returns {JSX.Element} Rendered input field
 */
export const fieldRenderer = ({
  input,
  id,
  pattern,
  inputMode,
  meta,
  type,
  placeholder,
  label,
  cls,
  readonly = false,
  onClick,
  maxLength,
  isDisabled,
  isLoading,
  placeholderOnly,
  onClickClear,
  autoComplete,
}) => {
  const { t } = useTranslation('errors');
  const errorMessage = t('field', { context: meta.error });

  const resolvedLabel = label || placeholder;
  // Passing the same text as label and placeholder renders it twice: the label floats
  // above the field while the native placeholder still shows inside it. Dropping the
  // duplicate lets InputRedux render the label in the placeholder position instead.
  // `placeholderOnly` fields never float a label, so their native placeholder is the only
  // text left once the field is focused or disabled: keep it.
  const resolvedPlaceholder = !placeholderOnly && resolvedLabel === placeholder ? '' : placeholder;

  return (
    <div className={`field-renderer ${cls}`}>
      <InputRedux
        {...input}
        id={id}
        type={type}
        label={resolvedLabel}
        placeholder={resolvedPlaceholder}
        onClick={onClick}
        className={`form-input ${feedbackClass(meta)}`}
        readOnly={readonly}
        maxLength={maxLength}
        isDisabled={isDisabled}
        isLoading={isLoading}
        {...(pattern && { pattern })}
        {...(inputMode && { inputMode })}
        hasError={hasFeedback(meta)}
        errorMessage={errorMessage}
        placeholderOnly={placeholderOnly}
        onClickClear={onClickClear}
        // InputRedux spreads this onto the DOM element: it is the only channel for
        // attributes it does not declare itself, such as autocomplete.
        {...(autoComplete && { input: { autoComplete } })}
      />
    </div>
  );
};

fieldRenderer.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  readonly: PropTypes.bool,
  onClick: PropTypes.func,
  cls: PropTypes.string,
  maxLength: PropTypes.string,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  pattern: PropTypes.string,
  inputMode: PropTypes.string,
  placeholderOnly: PropTypes.bool,
  label: PropTypes.string,
  autoComplete: PropTypes.string,
};

fieldRenderer.defaultProps = {
  cls: '',
  isDisabled: false,
  isLoading: false,
};

/**
 * selectRenderer component.
 * Renders a select dropdown with validation and error handling.
 *
 * @param {object} props - input, meta, options, cls, isDisabled, placeholder, label
 * @param {object} props.input - Redux Form input object
 * @param {object} props.meta - Redux Form meta object containing validation state
 * @param {Array} props.options - Select options array
 * @param {string} [props.cls=''] - Additional CSS class names
 * @param {boolean} [props.isDisabled=false] - Whether the select is disabled
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.label] - Select label
 * @returns {JSX.Element} Rendered select field
 */
export const selectRenderer = ({ input, meta, options, cls, isDisabled, placeholder, label }) => {
  const { t } = useTranslation('errors');
  const errorMessage = t('field', { context: meta.error });
  return (
    <div className={`${cls}`}>
      <SelectRedux
        options={options}
        {...input}
        isDisabled={isDisabled}
        label={label}
        placeholder={placeholder}
        hasError={hasFeedback(meta)}
        errorMessage={errorMessage}
      />
    </div>
  );
};

selectRenderer.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
  cls: PropTypes.string,
  isDisabled: PropTypes.bool,
  placeholder: PropTypes.string,
  label: PropTypes.string,
};

selectRenderer.defaultProps = {
  isDisabled: false,
};

/**
 * Renders a country selector with a flag icon and a dropdown for selecting country codes.
 *
 * @param {Object} params - The parameters for the renderer.
 * @param {Object} params.input - The input object containing value and onChange handler.
 * @param {Object} params.meta - The meta object containing error and touched states.
 * @param {boolean} params.isDisabled - Flag to disable the select input.
 * @param {string} params.cls - Additional CSS classes for styling.
 * @param {Array} params.options - The options for the select dropdown.
 * @returns {JSX.Element} The rendered country selector component.
 */
export const countryRenderer = ({ input, meta, isDisabled, cls, options }) => {
  const { t } = useTranslation('');
  const hasError = Boolean(meta.error);
  const errorMessage = t('errors:field', { context: meta.error });
  const countryFlagCdn = `https://cdn.jsdelivr.net/gh/lipis/flag-icon-css@master/flags/4x3/${input.value?.toLowerCase()}.svg`;

  return (
    <div className={`${cls} country-code`}>
      <div className="flag-container">
        <img src={countryFlagCdn} alt={input.value} className="flag-container-img" />
      </div>
      <SelectRedux
        options={options}
        label={t('passengers:label.phone_code')}
        placeholder={t('passengers:label.phone_code')}
        hasError={meta.touched && hasError}
        errorMessage={errorMessage}
        {...input}
        isDisabled={isDisabled}
        borderRadius="M"
      />
    </div>
  );
};

countryRenderer.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  children: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  cls: PropTypes.string,
};

countryRenderer.defaultProps = {
  cls: '',
};

/**
 * FilterableSelectRenderer component.
 * Renders a filterable select component with search functionality.
 *
 * @param {object} props - input, meta, and additional props
 * @param {object} props.input - Redux Form input object with value and onChange
 * @param {object} props.meta - Redux Form meta object containing validation state
 * @returns {JSX.Element} Rendered filterable select component
 */
export const FilterableSelectRenderer = ({ input, meta, ...rest }) => {
  return <FilterableSelect value={input.value} onChange={input.onChange} meta={meta} {...rest} />;
};

FilterableSelectRenderer.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }).isRequired,
};

/**
 * Get the list of expiration months.
 * Used to populate the expiration month select options on card forms.
 *
 * @returns {Array} The list of expiration months.
 */
export function getExpirationMonths() {
  return moment.months().map((monthName, index) => {
    const monthNumber = `0${index + 1}`.slice(-2);

    return {
      display: `${monthNumber} - ${monthName}`,
      value: monthNumber,
    };
  });
}

/**
 * Get the list of expiration years.
 * Used to populate the expiration year select options on card forms.
 *
 * @returns {Array} The list of expiration years.
 */
export function getExpirationYears() {
  const currentYear = moment().year();
  return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((count) => currentYear + count);
}

/**
 * Toggle renderer component for Redux Form
 * @param {object} props - Component props
 * @param {object} props.input - Redux Form input object
 * @param {string} props.id - Unique identifier for the toggle
 * @param {string} props.label - Text label to display next to the toggle
 * @param {boolean} props.disabled - Whether the toggle is disabled
 * @param {boolean} props.isLoading - Whether the toggle is in loading state
 * @returns {JSX.Element} - Rendered toggle component
 */
export const toggleRenderer = ({ input, id, label, disabled, isLoading }) => (
  <LabeledToggle
    label={label}
    checked={input.value}
    isLoading={isLoading}
    id={id}
    onChange={input.onChange}
    disabled={disabled}
    justifyContent="flex-end"
    fullWidth
  />
);

toggleRenderer.propTypes = {
  input: PropTypes.object.isRequired,
  id: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
};

/**
 * Card renderer component.
 * Renders a card input field with validation and error handling.
 *
 * @param {object} props - input, id, pattern, inputMode, meta, type, placeholder, label, cls, readonly, onClick, maxLength, isDisabled, placeholderOnly, onClickClear
 * @param {object} props.input - Redux Form input object
 * @param {string} props.id - Input ID
 * @param {string} props.pattern - Input pattern for validation
 * @param {string} props.inputMode - Input mode
 * @param {object} props.meta - Redux Form meta object containing validation state
 * @param {string} props.type - Input type
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.label - Input label
 * @param {string} props.cls - Additional CSS class names
 * @param {boolean} [props.readonly=false] - Whether the input is read-only
 * @param {Function} [props.onClick] - Click handler function
 * @param {string} [props.maxLength] - Maximum input length
 * @param {boolean} [props.isDisabled=false] - Whether the input is disabled
 * @param {boolean} [props.placeholderOnly] - Show only placeholder
 * @param {Function} [props.onClickClear] - Clear input handler
 * @returns {JSX.Element} Rendered card input field
 */
export const cardRenderer = ({
  input,
  id,
  pattern,
  inputMode,
  meta,
  type,
  placeholder,
  label,
  cls,
  readonly = false,
  onClick,
  maxLength,
  isDisabled,
  placeholderOnly,
  onClickClear,
}) => {
  const { t } = useTranslation('errors');
  const errorMessage = t('field', { context: meta.error });

  return (
    <div className={`field-renderer ${cls}`}>
      <InputCardRedux
        {...input}
        id={id}
        type={type}
        label={label || placeholder}
        placeholder={placeholder}
        onClick={onClick}
        className={`form-input ${feedbackClass(meta)}`}
        readOnly={readonly}
        maxLength={maxLength}
        isDisabled={isDisabled}
        {...(pattern && { pattern })}
        {...(inputMode && { inputMode })}
        hasError={hasFeedback(meta)}
        errorMessage={errorMessage}
        placeholderOnly={placeholderOnly}
        onClickClear={onClickClear}
      />
    </div>
  );
};

cardRenderer.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  readonly: PropTypes.bool,
  onClick: PropTypes.func,
  cls: PropTypes.string,
  maxLength: PropTypes.string,
  isDisabled: PropTypes.bool,
  pattern: PropTypes.string,
  inputMode: PropTypes.string,
  placeholderOnly: PropTypes.bool,
  label: PropTypes.string,
};

cardRenderer.defaultProps = {
  cls: '',
  isDisabled: false,
};
